import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import {
  VendorEventDetail,
  PricingItem,
  vendorGetEvent,
  vendorUpdatePricing,
} from "../vendorApi";

/* ═══════════════════════════════════════════════════════════════════════════
   EVENT DETAIL DRAWER — Right side panel with pricing editor
   ═══════════════════════════════════════════════════════════════════════════ */

const T = {
  navy: "#0f172a",
  navyLight: "#1e293b",
  gold: "#d4a373",
  white: "#ffffff",
  bg: "#f8fafc",
  slate: "#64748b",
  slateLt: "#94a3b8",
  line: "#e2e8f0",
  green: "#16a34a",
  greenBg: "#f0fdf4",
  greenBorder: "#bbf7d0",
  red: "#dc2626",
  redBg: "#fef2f2",
  redBorder: "#fecaca",
};

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  pending: { bg: "#fef3c7", color: "#92400e" },
  confirmed: { bg: "#dbeafe", color: "#1e40af" },
  completed: { bg: "#dcfce7", color: "#166534" },
};

interface Props {
  open: boolean;
  eventId: number | null;
  onClose: () => void;
  onPricingUpdated: () => void;
}

const EventDetailDrawer: React.FC<Props> = ({
  open,
  eventId,
  onClose,
  onPricingUpdated,
}) => {
  const [event, setEvent] = useState<VendorEventDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  /* Local price edits — keyed by pricing item id */
  const [priceEdits, setPriceEdits] = useState<Record<number, string>>({});
  const [hasChanges, setHasChanges] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setSaved(false);
    try {
      const data = await vendorGetEvent(eventId);
      setEvent(data);
      // Initialize edits from current vendor prices
      const edits: Record<number, string> = {};
      data.pricing.forEach((p) => {
        edits[p.id] = String(p.vendor_price);
      });
      setPriceEdits(edits);
      setHasChanges(false);
    } catch {
      setEvent(null);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (open && eventId) fetchDetail();
  }, [open, eventId, fetchDetail]);

  const handlePriceChange = (id: number, value: string) => {
    setPriceEdits((prev) => ({ ...prev, [id]: value }));
    setHasChanges(true);
    setSaved(false);
  };

  const handleSave = async () => {
    if (!event) return;
    setSaving(true);
    try {
      const prices = Object.entries(priceEdits)
        .filter(([id]) => {
          const item = event.pricing.find((p) => p.id === Number(id));
          return item && item.item_type === "vendor" && item.adjustable;
        })
        .map(([id, value]) => ({
          id: Number(id),
          vendor_price: parseFloat(value) || 0,
        }));

      await vendorUpdatePricing(event.id, prices);
      setSaved(true);
      setHasChanges(false);
      onPricingUpdated();
      // Refresh to get updated diff labels
      await fetchDetail();
    } catch {
      // Error handling
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d: string) => {
    const date = new Date(d + "T00:00:00");
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const getPriceDiff = (item: PricingItem): { diff: number; type: string } => {
    const currentPrice = parseFloat(priceEdits[item.id] || String(item.vendor_price)) || 0;
    const diff = Math.round((currentPrice - item.base_price) * 100) / 100;
    return {
      diff,
      type: diff < 0 ? "discount" : diff > 0 ? "increase" : "none",
    };
  };

  const pricingSummary = event?.pricing.reduce(
    (acc, item) => {
      const { diff } = getPriceDiff(item);
      return {
        totalBase: acc.totalBase + item.base_price,
        totalVendor:
          acc.totalVendor +
          (parseFloat(priceEdits[item.id] || String(item.vendor_price)) || 0),
        totalDiff: acc.totalDiff + diff,
      };
    },
    { totalBase: 0, totalVendor: 0, totalDiff: 0 }
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 520 },
          maxWidth: "100vw",
          bgcolor: T.bg,
          borderLeft: `1px solid ${T.line}`,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: T.navy,
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography
            sx={{ fontSize: 18, fontWeight: 700, color: T.white, mb: 0.5 }}
          >
            {loading ? "Loading..." : event?.event_name || "Event Details"}
          </Typography>
          {event && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Chip
                label={event.booking_ref}
                size="small"
                sx={{
                  height: 22,
                  fontSize: 10.5,
                  fontWeight: 600,
                  bgcolor: "rgba(255,255,255,0.1)",
                  color: "rgba(255,255,255,0.7)",
                }}
              />
              <Chip
                label={
                  event.status.charAt(0).toUpperCase() + event.status.slice(1)
                }
                size="small"
                sx={{
                  height: 22,
                  fontSize: 10.5,
                  fontWeight: 600,
                  bgcolor: STATUS_COLORS[event.status]?.bg || "#f1f5f9",
                  color: STATUS_COLORS[event.status]?.color || T.slate,
                }}
              />
            </Box>
          )}
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: "rgba(255,255,255,0.5)", "&:hover": { color: T.white } }}
        >
          <CloseRoundedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", pb: "100px" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 10,
            }}
          >
            <CircularProgress size={28} sx={{ color: T.gold }} />
          </Box>
        ) : !event ? (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <Typography sx={{ fontSize: 14, color: T.slateLt }}>
              Failed to load event details
            </Typography>
          </Box>
        ) : (
          <>
            {/* ── Event Info Cards ── */}
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Info grid */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1.5,
                }}
              >
                <InfoCard
                  icon={<CalendarTodayOutlinedIcon />}
                  label="Date & Time"
                  value={formatDate(event.event_date)}
                  sub={event.event_time}
                />
                <InfoCard
                  icon={<MeetingRoomOutlinedIcon />}
                  label="Hall"
                  value={event.halls.map((h) => h.label).join(", ") || "N/A"}
                  sub={`Cap: ${event.halls.reduce((a, h) => a + h.capacity, 0)}`}
                />
                <InfoCard
                  icon={<PersonOutlinedIcon />}
                  label="Customer"
                  value={event.customer?.name || "N/A"}
                  sub={event.customer?.organization || event.customer?.email || undefined}
                />
                <InfoCard
                  icon={<GroupOutlinedIcon />}
                  label="Guests"
                  value={String(event.guest_count)}
                  sub={event.children_count > 0 ? `${event.children_count} children` : undefined}
                />
              </Box>

              {/* ── Selected Services ── */}
              {event.services.length > 0 && (
                <Box
                  sx={{
                    bgcolor: T.white,
                    borderRadius: "12px",
                    border: `1px solid ${T.line}`,
                    p: 2,
                  }}
                >
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 700, color: T.slate, textTransform: "uppercase", letterSpacing: "0.04em", mb: 1.5 }}
                  >
                    Selected Services
                  </Typography>
                  {event.services.map((svc, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        py: 1,
                        borderTop: i > 0 ? `1px solid ${T.line}` : "none",
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: T.navy }}>
                          {svc.value}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: T.slateLt }}>
                          {svc.label}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy }}>
                        ${svc.price.toFixed(2)}
                        <Typography component="span" sx={{ fontSize: 10, color: T.slateLt, ml: 0.5 }}>
                          {svc.type === "pp" ? "/pp" : "flat"}
                        </Typography>
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* ── Pricing Section ── */}
              <Box
                sx={{
                  bgcolor: T.white,
                  borderRadius: "12px",
                  border: `1px solid ${T.line}`,
                  p: 2,
                }}
              >
                <Typography
                  sx={{ fontSize: 12, fontWeight: 700, color: T.slate, textTransform: "uppercase", letterSpacing: "0.04em", mb: 1.5 }}
                >
                  Pricing Breakdown
                </Typography>

                {event.pricing.length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: T.slateLt, py: 2, textAlign: "center" }}>
                    No pricing items configured
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {event.pricing.map((item) => {
                      const { diff, type } = getPriceDiff(item);
                      const isVendor = item.item_type === "vendor";
                      const canEdit = isVendor && item.adjustable;

                      return (
                        <Box
                          key={item.id}
                          sx={{
                            p: 2,
                            borderRadius: "10px",
                            border: `1px solid ${T.line}`,
                            bgcolor: canEdit ? T.white : "#f8fafc",
                          }}
                        >
                          {/* Item header */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mb: 1.2,
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy }}>
                                {item.item_name}
                              </Typography>
                              {isVendor && (
                                <Chip
                                  label="Vendor"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: 9,
                                    fontWeight: 700,
                                    bgcolor: "rgba(212,163,115,0.12)",
                                    color: T.gold,
                                  }}
                                />
                              )}
                              {!item.adjustable && (
                                <Chip
                                  label="Read Only"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: 9,
                                    fontWeight: 600,
                                    bgcolor: "#f1f5f9",
                                    color: T.slateLt,
                                  }}
                                />
                              )}
                            </Box>
                            <Typography sx={{ fontSize: 10, color: T.slateLt }}>
                              {item.pricing_type === "pp" ? "Per Person" : "Flat Rate"}
                            </Typography>
                          </Box>

                          {/* Price row */}
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 1.5,
                            }}
                          >
                            {/* Base price */}
                            <Box>
                              <Typography
                                sx={{ fontSize: 10.5, fontWeight: 600, color: T.slateLt, mb: 0.5 }}
                              >
                                Base Price
                              </Typography>
                              <Typography
                                sx={{ fontSize: 16, fontWeight: 700, color: T.slate }}
                              >
                                ${item.base_price.toFixed(2)}
                              </Typography>
                            </Box>

                            {/* Vendor price (editable or read-only) */}
                            <Box>
                              <Typography
                                sx={{ fontSize: 10.5, fontWeight: 600, color: T.slateLt, mb: 0.5 }}
                              >
                                Vendor Price
                              </Typography>
                              {canEdit ? (
                                <TextField
                                  type="number"
                                  size="small"
                                  value={priceEdits[item.id] ?? item.vendor_price}
                                  onChange={(e) =>
                                    handlePriceChange(item.id, e.target.value)
                                  }
                                  InputProps={{
                                    startAdornment: (
                                      <Typography
                                        sx={{
                                          fontSize: 14,
                                          fontWeight: 600,
                                          color: T.slateLt,
                                          mr: 0.3,
                                        }}
                                      >
                                        $
                                      </Typography>
                                    ),
                                  }}
                                  sx={{
                                    width: "100%",
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "8px",
                                      fontSize: 15,
                                      fontWeight: 700,
                                      color: T.navy,
                                      bgcolor: T.white,
                                      "& fieldset": {
                                        borderColor: T.gold,
                                        borderWidth: 1.5,
                                      },
                                      "&:hover fieldset": {
                                        borderColor: T.gold,
                                      },
                                    },
                                    "& input": { py: 0.8 },
                                  }}
                                />
                              ) : (
                                <Typography
                                  sx={{
                                    fontSize: 16,
                                    fontWeight: 700,
                                    color: T.navy,
                                  }}
                                >
                                  ${(
                                    parseFloat(
                                      priceEdits[item.id] ??
                                        String(item.vendor_price)
                                    ) || 0
                                  ).toFixed(2)}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {/* Difference indicator */}
                          {diff !== 0 && (
                            <Box
                              sx={{
                                mt: 1.2,
                                px: 1.5,
                                py: 0.8,
                                borderRadius: "8px",
                                bgcolor:
                                  type === "discount" ? T.greenBg : T.redBg,
                                border: `1px solid ${
                                  type === "discount"
                                    ? T.greenBorder
                                    : T.redBorder
                                }`,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.8,
                              }}
                            >
                              {type === "discount" ? (
                                <ArrowDownwardRoundedIcon
                                  sx={{ fontSize: 14, color: T.green }}
                                />
                              ) : (
                                <ArrowUpwardRoundedIcon
                                  sx={{ fontSize: 14, color: T.red }}
                                />
                              )}
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color:
                                    type === "discount" ? T.green : T.red,
                                }}
                              >
                                {type === "discount"
                                  ? "Discount Applied"
                                  : "Increased Price"}
                                : ${Math.abs(diff).toFixed(2)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>

              {/* Notes */}
              {event.notes && (
                <Box
                  sx={{
                    bgcolor: T.white,
                    borderRadius: "12px",
                    border: `1px solid ${T.line}`,
                    p: 2,
                  }}
                >
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 700, color: T.slate, textTransform: "uppercase", letterSpacing: "0.04em", mb: 1 }}
                  >
                    Notes
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: T.navy, lineHeight: 1.6 }}>
                    {event.notes}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>

      {/* ── Sticky Pricing Summary + Save ── */}
      {event && event.pricing.length > 0 && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: T.white,
            borderTop: `1px solid ${T.line}`,
            px: 3,
            py: 2,
            boxShadow: "0 -4px 20px rgba(0,0,0,0.06)",
          }}
        >
          {/* Summary row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 11, color: T.slateLt, fontWeight: 600 }}>
                Total Base
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.slate }}>
                ${pricingSummary?.totalBase.toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ fontSize: 11, color: T.slateLt, fontWeight: 600 }}>
                Difference
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color:
                    (pricingSummary?.totalDiff || 0) < 0
                      ? T.green
                      : (pricingSummary?.totalDiff || 0) > 0
                      ? T.red
                      : T.slate,
                }}
              >
                {(pricingSummary?.totalDiff || 0) > 0 ? "+" : ""}
                ${pricingSummary?.totalDiff.toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography sx={{ fontSize: 11, color: T.slateLt, fontWeight: 600 }}>
                Vendor Total
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: T.navy }}>
                ${pricingSummary?.totalVendor.toFixed(2)}
              </Typography>
            </Box>
          </Box>

          {/* Save button */}
          <Button
            fullWidth
            variant="contained"
            disabled={!hasChanges || saving}
            onClick={handleSave}
            startIcon={
              saved ? (
                <CheckCircleOutlinedIcon sx={{ fontSize: 16 }} />
              ) : (
                <SaveOutlinedIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{
              py: 1.3,
              borderRadius: "10px",
              fontSize: 13,
              fontWeight: 700,
              textTransform: "none",
              bgcolor: saved ? T.green : T.navy,
              color: T.white,
              boxShadow: "0 2px 8px rgba(15,23,42,0.2)",
              "&:hover": {
                bgcolor: saved ? "#15803d" : T.navyLight,
              },
              "&.Mui-disabled": {
                bgcolor: "#e2e8f0",
                color: "#94a3b8",
              },
            }}
          >
            {saving ? (
              <CircularProgress size={18} sx={{ color: T.white }} />
            ) : saved ? (
              "Saved Successfully"
            ) : hasChanges ? (
              "Save Pricing Changes"
            ) : (
              "No Changes"
            )}
          </Button>
        </Box>
      )}
    </Drawer>
  );
};

/* ── Small info card component ── */
const InfoCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}> = ({ icon, label, value, sub }) => (
  <Box
    sx={{
      bgcolor: "#ffffff",
      borderRadius: "12px",
      border: "1px solid #e2e8f0",
      p: 2,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.8,
        mb: 1,
        "& .MuiSvgIcon-root": { fontSize: 14, color: "#94a3b8" },
      }}
    >
      {icon}
      <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </Typography>
    </Box>
    <Typography
      sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}
    >
      {value}
    </Typography>
    {sub && (
      <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.3 }}>
        {sub}
      </Typography>
    )}
  </Box>
);

export default EventDetailDrawer;
